import { useEffect, useState } from "react";
import {
  BACKEND,
  getToken,
  setToken,
  clearToken,
  adminFetch,
  resolveMedia,
} from "@/lib/api";
import { youTubeId, youTubeThumb, videoSource } from "@/lib/youtube";
import { INDUSTRIES } from "@/data/industries";

const input =
  "w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#1E2225] outline-none focus:border-[#04785C]";
const label = "block text-[11px] font-semibold uppercase tracking-[0.12em] text-[#6B6358] mb-1.5";
const btn = "rounded-full px-4 py-2 text-sm font-semibold transition-colors";
const ember = `${btn} bg-[#00F5A0] text-[#282D32] hover:bg-[#06D6A0]`;
const ghost = `${btn} border border-black/15 text-[#1E2225] hover:bg-black/5`;

const blank = {
  title: "", slug: "", type: "", client: "", vertical: "", category: "",
  date: "", timeframe: "", project_type: "", services: [], live_url: "",
  agent: "Studio Agent", poster: "", video_src: "", youtube_url: "", blurb: "", overview: "",
  challenge: "", solution: "", results: "", final_thoughts: "",
  metrics: [], gallery: [], credits: [], featured: false, published: true, show_in_customers: true,
  team: "Marketing & Creative", teams: [], tags: [], order: 0,
};

function UploadField({ value, onChange, accept = "image/*,video/*", placeholder }) {
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await adminFetch("/admin/upload", { method: "POST", body: fd, isForm: true });
      onChange(r.url);
    } catch (err) {
      alert("Upload failed: " + err.message);
    }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2">
      <input className={input} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      <label className={`${ghost} cursor-pointer whitespace-nowrap`}>
        {busy ? "…" : "Upload"}
        <input type="file" accept={accept} className="hidden" onChange={up} disabled={busy} />
      </label>
    </div>
  );
}

const VERTICALS = Object.values(INDUSTRIES).map((i) => i.name);
const TAG_OPTIONS = [
  // Existing (creative/video)
  "Ad Creative", "Motion Design", "AI Avatar / Spokesperson", "Short-Form Social",
  "Brand Film", "Product Video", "Listing Reel", "Social Campaign", "Explainer",
  "Testimonial", "Ad Spot", "Product Demo", "Case Study", "Website",
  // Marketing & Growth
  "Paid Ads / Media Buying", "Content Strategy", "Email Marketing", "SEO",
  "Photography", "Podcast",
  // Workflow & Automation
  "Workflow Automation", "CRM Setup / Integration", "Lead Routing",
  "Email / SMS Automation", "Chatbot / Voice Agent", "Appointment / Scheduling System",
  "Reputation / Review System", "Reporting Dashboard", "Data Pipeline / Migration",
  // Product & Engineering
  "Custom Application", "SaaS Product", "AI / LLM Development", "Web App",
  "API / Integration", "MVP Build",
];
const TEAM_OPTIONS = ["Marketing & Creative", "Workflow & Automation", "Product & Engineering"];
const TIMEFRAME_OPTIONS = ["1 week", "2 weeks", "3 weeks", "4 weeks", "1 month", "2 months", "3 months", "Ongoing"];
const YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => String(new Date().getFullYear() + 1 - i));

// Dropdown that always preserves an existing custom value as a selectable option.
const SelectWithCustom = ({ value, onChange, options, placeholder }) => (
  <select className={input} value={value} onChange={onChange}>
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
    {value && !options.includes(value) && <option value={value}>{value}</option>}
  </select>
);

// Fixed multi-select rendered as toggleable chips. Stores an array of strings.
const MultiChips = ({ options, value = [], onChange, testId }) => {
  const toggle = (o) =>
    onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => toggle(o)}
            data-testid={`${testId}-${o}`}
            aria-pressed={on}
            className={`rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors ${
              on
                ? "border-[#00F5A0] bg-[#00F5A0] text-[#282D32]"
                : "border-black/15 bg-white text-[#1E2225] hover:border-[#04785C]"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
};

function ProjectForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState({
    ...blank,
    ...initial,
    services: initial?.services || [],
    tags: initial?.tags || [],
    teams: initial?.teams?.length ? initial.teams : initial?.team ? [initial.team] : [],
  });
  const [saving, setSaving] = useState(false);
  const [yt, setYt] = useState({ status: "", meta: null });
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const fetchYt = async (force) => {
    const url = (f.youtube_url || "").trim();
    if (!url) return;
    setYt({ status: "loading", meta: null });
    try {
      const m = await adminFetch(`/admin/youtube-meta?url=${encodeURIComponent(url)}`);
      setF((prev) => ({
        ...prev,
        title: force || !prev.title ? m.title || prev.title : prev.title,
        poster: force || !prev.poster ? m.thumbnail_url || prev.poster : prev.poster,
      }));
      setYt({ status: "done", meta: m });
    } catch {
      setYt({ status: "error", meta: null });
    }
  };

  useEffect(() => {
    const url = (f.youtube_url || "").trim();
    if (!/(?:youtube\.com|youtu\.be|vimeo\.com)/.test(url)) {
      setYt({ status: "", meta: null });
      return;
    }
    const t = setTimeout(() => fetchYt(false), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.youtube_url]);

  const save = async () => {
    if (!f.title.trim()) return alert("Title is required");
    setSaving(true);
    try {
      const body = { ...f, order: Number(f.order) || 0 };
      if (f.id) await adminFetch(`/admin/projects/${f.id}`, { method: "PUT", body });
      else await adminFetch("/admin/projects", { method: "POST", body });
      onSaved();
    } catch (e) {
      alert("Save failed: " + e.message);
    }
    setSaving(false);
  };

  const arr = (key, empty) => ({
    add: () => setF({ ...f, [key]: [...f[key], empty] }),
    rm: (i) => setF({ ...f, [key]: f[key].filter((_, x) => x !== i) }),
    set: (i, patch) => setF({ ...f, [key]: f[key].map((it, x) => (x === i ? { ...it, ...patch } : it)) }),
  });
  const metrics = arr("metrics", { value: "", label: "" });
  const credits = arr("credits", { name: "", role: "" });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{f.id ? "Edit project" : "New project"}</h2>
        <div className="flex gap-2">
          <button className={ghost} onClick={onCancel}>Cancel</button>
          <button className={ember} onClick={save} disabled={saving} data-testid="admin-save-project">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div><span className={label}>Title</span><input className={input} value={f.title} onChange={s("title")} data-testid="admin-field-title" /></div>
        <div><span className={label}>Slug (optional)</span><input className={input} value={f.slug} onChange={s("slug")} placeholder="auto from title" /></div>
        <div><span className={label}>Client</span><input className={input} value={f.client} onChange={s("client")} /></div>
        <div><span className={label}>Vertical</span>
          <SelectWithCustom value={f.vertical} onChange={s("vertical")} options={VERTICALS} placeholder="— Select vertical —" />
        </div>
        <div><span className={label}>Released (year)</span>
          <SelectWithCustom value={f.date} onChange={s("date")} options={YEAR_OPTIONS} placeholder="— Select year —" />
        </div>
        <div><span className={label}>Timeframe</span>
          <SelectWithCustom value={f.timeframe} onChange={s("timeframe")} options={TIMEFRAME_OPTIONS} placeholder="— Select timeframe —" />
        </div>
        <div><span className={label}>Agent</span><input className={input} value={f.agent} onChange={s("agent")} /></div>
        <div><span className={label}>Live URL</span><input className={input} value={f.live_url} onChange={s("live_url")} /></div>
        <div><span className={label}>Order</span><input className={input} type="number" value={f.order} onChange={s("order")} /></div>
        <div><span className={label}>Services (comma separated)</span><input className={input} value={f.services.join(", ")} onChange={(e) => setF({ ...f, services: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} /></div>
      </div>

      <div>
        <span className={label}>Project tags (pick all that apply)</span>
        <MultiChips options={TAG_OPTIONS} value={f.tags} onChange={(v) => setF({ ...f, tags: v })} testId="admin-field-tags" />
      </div>

      <div>
        <span className={label}>Teams (a project can power more than one)</span>
        <MultiChips options={TEAM_OPTIONS} value={f.teams} onChange={(v) => setF({ ...f, teams: v })} testId="admin-field-teams" />
      </div>

      <div><span className={label}>Poster / thumbnail image</span><UploadField value={f.poster} onChange={(v) => setF({ ...f, poster: v })} accept="image/*" placeholder="image URL or upload — used as the MP4 poster" />
        {f.poster && <img src={resolveMedia(f.poster)} alt="" className="mt-2 h-28 rounded-lg object-cover" />}</div>
      <div><span className={label}>Reel video (mp4 fallback)</span><UploadField value={f.video_src} onChange={(v) => setF({ ...f, video_src: v })} accept="video/*" placeholder="video URL or upload" /></div>
      <div><span className={label}>Video URL (YouTube, Vimeo, or MP4)</span>
        <div className="flex items-center gap-2">
          <input className={input} value={f.youtube_url} onChange={s("youtube_url")} placeholder="YouTube, Vimeo, or a direct .mp4 link" data-testid="admin-field-youtube" />
          <button type="button" className={`${ghost} whitespace-nowrap`} onClick={() => fetchYt(true)} data-testid="admin-youtube-fetch">Auto-fill</button>
        </div>
        {yt.status === "loading" && <p className="mt-1 text-[12px] text-[#6B6358]">Fetching video details…</p>}
        {yt.status === "error" && <p className="mt-1 text-[12px] text-[#04785C]">Couldn't fetch that link — check the URL. (Direct MP4 links can't auto-fill — set the Poster image above.)</p>}
        {yt.status === "done" && yt.meta && (
          <div className="mt-2 flex items-center gap-3" data-testid="admin-youtube-preview">
            <img
              src={yt.meta.thumbnail_url}
              onError={(e) => { if (yt.meta.thumbnail_fallback) e.currentTarget.src = yt.meta.thumbnail_fallback; }}
              alt=""
              className="h-12 w-20 rounded object-cover"
            />
            <p className="text-[13px] text-[#1E2225]">Pulled: {yt.meta.title}</p>
          </div>
        )}
        <p className="mt-1 text-[12px] text-[#6B6358]">Paste a YouTube or Vimeo link (Auto-fill grabs the title + thumbnail), or a direct .mp4 URL (set the Poster image above as its thumbnail).</p>
      </div>

      <div><span className={label}>Blurb (gallery card)</span><textarea className={input} rows={2} value={f.blurb} onChange={s("blurb")} /></div>
      <div><span className={label}>Overview</span><textarea className={input} rows={2} value={f.overview} onChange={s("overview")} /></div>
      <div><span className={label}>The challenge</span><textarea className={input} rows={2} value={f.challenge} onChange={s("challenge")} /></div>
      <div><span className={label}>The solution</span><textarea className={input} rows={2} value={f.solution} onChange={s("solution")} /></div>
      <div><span className={label}>The result</span><textarea className={input} rows={2} value={f.results} onChange={s("results")} /></div>
      <div><span className={label}>Final thoughts</span><textarea className={input} rows={2} value={f.final_thoughts} onChange={s("final_thoughts")} /></div>

      {/* Gallery */}
      <div>
        <div className="mb-2 flex items-center justify-between"><span className={label}>Gallery images</span>
          <button className={ghost} onClick={() => setF({ ...f, gallery: [...f.gallery, ""] })}>+ Add image</button></div>
        <div className="space-y-2">
          {f.gallery.map((g, i) => (
            <div key={i} className="flex items-center gap-2">
              <UploadField value={g} onChange={(v) => setF({ ...f, gallery: f.gallery.map((x, j) => (j === i ? v : x)) })} accept="image/*" />
              <button className={ghost} onClick={() => setF({ ...f, gallery: f.gallery.filter((_, j) => j !== i) })}>✕</button>
            </div>
          ))}
        </div>
      </div>

      {/* Metrics */}
      <div>
        <div className="mb-2 flex items-center justify-between"><span className={label}>Result metrics</span>
          <button className={ghost} onClick={metrics.add}>+ Add metric</button></div>
        <div className="space-y-2">
          {f.metrics.map((m, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className={input} placeholder="92%" value={m.value} onChange={(e) => metrics.set(i, { value: e.target.value })} />
              <input className={input} placeholder="View-through" value={m.label} onChange={(e) => metrics.set(i, { label: e.target.value })} />
              <button className={ghost} onClick={() => metrics.rm(i)}>✕</button>
            </div>
          ))}
        </div>
      </div>

      {/* Credits */}
      <div>
        <div className="mb-2 flex items-center justify-between"><span className={label}>Credits</span>
          <button className={ghost} onClick={credits.add}>+ Add credit</button></div>
        <div className="space-y-2">
          {f.credits.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className={input} placeholder="Name" value={c.name} onChange={(e) => credits.set(i, { name: e.target.value })} />
              <input className={input} placeholder="Role" value={c.role} onChange={(e) => credits.set(i, { role: e.target.value })} />
              <button className={ghost} onClick={() => credits.rm(i)}>✕</button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-6">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> Featured</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="admin-field-published" /> Published</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.show_in_customers} onChange={(e) => setF({ ...f, show_in_customers: e.target.checked })} data-testid="admin-field-customers" /> Show in Customers</label>
      </div>
    </div>
  );
}

function Categories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const reload = () => fetch(`${BACKEND}/api/categories`).then((r) => r.json()).then(setCats);
  useEffect(() => { reload(); }, []);
  const add = async () => {
    if (!name.trim()) return;
    await adminFetch("/admin/categories", { method: "POST", body: { name, order: cats.length } });
    setName(""); reload();
  };
  const del = async (id) => { await adminFetch(`/admin/categories/${id}`, { method: "DELETE" }); reload(); };
  const rename = async (c, v) => { await adminFetch(`/admin/categories/${c.id}`, { method: "PUT", body: { name: v, order: c.order } }); reload(); };
  return (
    <div className="max-w-xl space-y-4">
      <h2 className="text-xl font-semibold">Categories</h2>
      <div className="flex gap-2">
        <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="New category" data-testid="admin-cat-name" />
        <button className={ember} onClick={add} data-testid="admin-cat-add">Add</button>
      </div>
      <div className="space-y-2">
        {cats.map((c) => (
          <div key={c.id} className="flex items-center gap-2 rounded-lg border border-black/10 bg-white p-2">
            <input className={`${input} border-transparent`} defaultValue={c.name} onBlur={(e) => e.target.value !== c.name && rename(c, e.target.value)} />
            <button className={ghost} onClick={() => del(c.id)}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DemoAnalytics() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  const load = () =>
    adminFetch("/admin/demo-stats")
      .then(setStats)
      .catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  if (err) return <p className="text-sm text-[#04785C]">Failed to load: {err}</p>;
  if (!stats) return <p className="text-sm text-[#6B6358]">Loading analytics…</p>;

  const maxAgent = Math.max(1, ...stats.by_agent.map((a) => a.count));
  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="space-y-10" data-testid="admin-demo-analytics">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Demo interest</h2>
          <p className="mt-1 text-sm text-[#6B6358]">
            Which agents prospects open across the industry pages.
          </p>
        </div>
        <button className={ghost} onClick={load} data-testid="admin-demo-refresh">Refresh</button>
      </div>

      {/* Summary */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-black/10 bg-white p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#6B6358]">Total demo opens</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight text-[#1E2225]" data-testid="admin-demo-total">{stats.total}</p>
        </div>
        <div className="rounded-xl border border-black/10 bg-white p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#6B6358]">Top agent</p>
          <p className="mt-2 text-lg font-semibold tracking-tight text-[#1E2225]">{stats.by_agent[0]?.agent || "—"}</p>
        </div>
        <div className="rounded-xl border border-black/10 bg-white p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#6B6358]">Top industry</p>
          <p className="mt-2 text-lg font-semibold tracking-tight text-[#1E2225]">{stats.by_industry[0]?.industry || "—"}</p>
        </div>
      </div>

      {stats.total === 0 && (
        <p className="rounded-xl border border-dashed border-black/15 bg-white p-6 text-sm text-[#6B6358]">
          No demo opens tracked yet. Open a "Watch Demo" or "LIVE DEMO" on any industry page and it will appear here.
        </p>
      )}

      {/* By agent — ranked bars */}
      {stats.by_agent.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-[#6B6358]">Most-opened agents</h3>
          <div className="mt-4 space-y-2">
            {stats.by_agent.map((a) => (
              <div key={a.agent + a.agent_key} className="flex items-center gap-3" data-testid={`admin-demo-agent-${a.agent_key}`}>
                <span className="w-52 shrink-0 truncate text-sm font-medium text-[#1E2225]">{a.agent}</span>
                <div className="h-6 flex-1 overflow-hidden rounded-full bg-black/5">
                  <div className="h-full rounded-full bg-[#04785C]" style={{ width: `${(a.count / maxAgent) * 100}%` }} />
                </div>
                <span className="w-10 shrink-0 text-right text-sm font-semibold text-[#1E2225]">{a.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By industry */}
      {stats.by_industry.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-[#6B6358]">By industry</h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {stats.by_industry.map((r) => (
              <div key={r.industry} className="flex items-center justify-between rounded-xl border border-black/10 bg-white px-4 py-3">
                <span className="text-sm font-medium text-[#1E2225]">{r.industry}</span>
                <span className="text-sm font-semibold text-[#04785C]">{r.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agent × industry breakdown */}
      {stats.matrix.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-[#6B6358]">Agent × industry breakdown</h3>
          <div className="mt-4 overflow-hidden rounded-xl border border-black/10">
            <table className="w-full text-sm">
              <thead className="bg-black/[0.03] text-left text-[12px] uppercase tracking-[0.1em] text-[#6B6358]">
                <tr><th className="px-4 py-3">Agent</th><th className="px-4 py-3">Industry</th><th className="px-4 py-3 text-right">Opens</th></tr>
              </thead>
              <tbody>
                {stats.matrix.map((r, i) => (
                  <tr key={i} className="border-t border-black/5 bg-white">
                    <td className="px-4 py-2.5 font-medium text-[#1E2225]">{r.agent}</td>
                    <td className="px-4 py-2.5 text-[#6B6358]">{r.industry}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-[#1E2225]">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent activity */}
      {stats.recent.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-[#6B6358]">Recent activity</h3>
          <div className="mt-4 space-y-2">
            {stats.recent.map((e) => (
              <div key={e.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-black/10 bg-white px-4 py-2.5 text-sm">
                <span className="font-medium text-[#1E2225]">{e.agent}</span>
                <span className="text-[#6B6358]">· {e.industry || "—"}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${e.mode === "phone" ? "bg-[#04785C]/10 text-[#04785C]" : "bg-black/10 text-[#6B6358]"}`}>
                  {e.mode === "phone" ? "Live demo" : "Watch demo"}
                </span>
                <span className="ml-auto text-[12px] text-[#6B6358]">{fmtTime(e.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const AGENT_ICON_OPTIONS = [
  "handshake", "megaphone", "star", "phone", "calendar",
  "clapperboard", "video", "users", "clipboard", "message", "wrench", "route",
];

const blankAgent = {
  name: "", badge: "", icon: "handshake", avatar: "", description: "",
  tags: [], cta: "watch", demo_video: "", phone: "", active: true, key: "", order: 0,
};

function AgentForm({ industry, initial, onSaved, onCancel }) {
  const [f, setF] = useState({ ...blankAgent, ...initial, tags: initial?.tags || [] });
  const [saving, setSaving] = useState(false);
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.name.trim()) return alert("Name is required");
    setSaving(true);
    try {
      const body = { ...f, industry, order: Number(f.order) || 0 };
      if (f.id) await adminFetch(`/admin/industry-agents/${f.id}`, { method: "PUT", body });
      else await adminFetch("/admin/industry-agents", { method: "POST", body });
      onSaved();
    } catch (e) {
      alert("Save failed: " + e.message);
    }
    setSaving(false);
  };

  return (
    <div className="rounded-2xl border border-black/10 bg-white p-6" data-testid="admin-agent-form">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{f.id ? "Edit agent" : "New agent"}</h3>
        <div className="flex gap-2">
          <button className={ghost} onClick={onCancel}>Cancel</button>
          <button className={ember} onClick={save} disabled={saving} data-testid="admin-agent-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div><span className={label}>Name</span><input className={input} value={f.name} onChange={s("name")} data-testid="admin-agent-name" /></div>
        <div><span className={label}>Badge</span><input className={input} value={f.badge} onChange={s("badge")} placeholder="Close deals 24/7" /></div>
        <div><span className={label}>Icon</span>
          <select className={input} value={f.icon} onChange={s("icon")}>
            {AGENT_ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
          </select>
        </div>
        <div><span className={label}>CTA type</span>
          <select className={input} value={f.cta} onChange={s("cta")} data-testid="admin-agent-cta">
            <option value="watch">Watch Demo (video)</option>
            <option value="live">Live Demo (phone)</option>
          </select>
        </div>
      </div>
      <div className="mt-4"><span className={label}>Description</span><textarea className={input} rows={2} value={f.description} onChange={s("description")} /></div>
      <div className="mt-4"><span className={label}>Capability tags (comma separated)</span>
        <input className={input} value={f.tags.join(", ")} onChange={(e) => setF({ ...f, tags: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
      </div>
      <div className="mt-4"><span className={label}>Avatar image</span>
        <UploadField value={f.avatar} onChange={(v) => setF({ ...f, avatar: v })} accept="image/*" placeholder="image URL or upload" />
        {f.avatar && <img src={resolveMedia(f.avatar)} alt="" className="mt-2 h-14 w-14 rounded-full object-cover" />}
      </div>
      {f.cta === "live" ? (
        <div className="mt-4"><span className={label}>Demo phone number</span><input className={input} value={f.phone} onChange={s("phone")} placeholder="+1 (800) 555-0100" data-testid="admin-agent-phone" /></div>
      ) : (
        <div className="mt-4"><span className={label}>Demo video (YouTube URL)</span><input className={input} value={f.demo_video} onChange={s("demo_video")} placeholder="https://www.youtube.com/watch?v=..." data-testid="admin-agent-video" /></div>
      )}
      <label className="mt-5 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="admin-agent-active" />
        Active — show this agent live on the industry page
      </label>
    </div>
  );
}

function AgentsManager() {
  const [industries, setIndustries] = useState([]);
  const [industry, setIndustry] = useState("");
  const [agents, setAgents] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND}/api/industries`).then((r) => r.json()).then((d) => {
      setIndustries(d);
      if (d[0]) setIndustry(d[0].key);
    });
  }, []);

  const reload = () => {
    if (!industry) return;
    adminFetch(`/admin/industries/${industry}/agents`).then(setAgents).catch(() => setAgents([]));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [industry]);

  const del = async (id) => {
    if (!window.confirm("Delete this agent?")) return;
    await adminFetch(`/admin/industry-agents/${id}`, { method: "DELETE" });
    reload();
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= agents.length) return;
    const next = [...agents];
    [next[i], next[j]] = [next[j], next[i]];
    setAgents(next);
    await adminFetch("/admin/industry-agents/reorder", {
      method: "POST",
      body: { industry, ids: next.map((a) => a.id) },
    });
  };

  const toggleActive = async (a) => {
    await adminFetch(`/admin/industry-agents/${a.id}`, {
      method: "PUT",
      body: { ...a, active: !a.active },
    });
    reload();
  };

  if (editing) {
    return (
      <AgentForm
        industry={industry}
        initial={editing.id ? editing : {}}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-agents-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={label + " !mb-0"}>Solution industry</span>
          <select
            className={`${input} w-auto`}
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            data-testid="admin-agents-industry"
          >
            {industries.map((ind) => <option key={ind.key} value={ind.key}>{ind.name}</option>)}
          </select>
        </div>
        <button className={ember} onClick={() => setEditing({})} data-testid="admin-agent-add">+ Add agent</button>
      </div>

      <p className="text-sm text-[#6B6358]">{agents.length} agents</p>

      <div className="space-y-2">
        {agents.map((a, i) => (
          <div key={a.id} className="flex items-center gap-4 rounded-xl border border-black/10 bg-white p-4" data-testid={`admin-agent-row-${a.key}`}>
            <img src={resolveMedia(a.avatar) || undefined} alt="" className={`h-11 w-11 rounded-full object-cover ${a.active ? "" : "opacity-40 grayscale"}`} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{a.name}</p>
              <p className="truncate text-[13px] text-[#6B6358]">{a.badge}</p>
            </div>
            <button
              onClick={() => toggleActive(a)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${a.active ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-black/10 text-[#6B6358] hover:bg-black/15"}`}
              data-testid={`admin-agent-toggle-${a.key}`}
              title="Click to show/hide on the industry page"
            >
              {a.active ? "Live" : "Hidden"}
            </button>
            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${a.cta === "live" ? "bg-[#04785C]/10 text-[#04785C]" : "bg-black/10 text-[#6B6358]"}`}>
              {a.cta === "live" ? "Phone" : "Video"}
            </span>
            <div className="flex flex-col">
              <button className="px-1 text-[#6B6358] hover:text-[#1E2225]" onClick={() => move(i, -1)} disabled={i === 0}>▲</button>
              <button className="px-1 text-[#6B6358] hover:text-[#1E2225]" onClick={() => move(i, 1)} disabled={i === agents.length - 1}>▼</button>
            </div>
            <button className={ghost} onClick={() => setEditing(a)} data-testid={`admin-agent-edit-${a.key}`}>Edit</button>
            <button className={ghost} onClick={() => del(a.id)}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DemoSection() {
  const [sub, setSub] = useState("agents");
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold">Demo</h2>
        <p className="mt-1 text-sm text-[#6B6358]">
          Manage the AI agents shown on each solution industry, and see which demos prospects open.
        </p>
      </div>
      <div className="flex gap-2">
        <button className={sub === "agents" ? ember : ghost} onClick={() => setSub("agents")} data-testid="admin-demo-sub-agents">Manage agents</button>
        <button className={sub === "interest" ? ember : ghost} onClick={() => setSub("interest")} data-testid="admin-demo-sub-interest">Demo interest</button>
      </div>
      {sub === "agents" ? <AgentsManager /> : <DemoAnalytics />}
    </div>
  );
}

const blankPost = {
  title: "", youtube_url: "", category: "", author: "The AI Dept.",
  excerpt: "", content: "", read_time: "", featured: false, published: true, order: 0,
};

function LabsForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState({ ...blankPost, ...initial });
  const [saving, setSaving] = useState(false);
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const thumb = youTubeThumb(f.youtube_url);

  const save = async () => {
    if (!f.title.trim()) return alert("Title is required");
    if (!youTubeId(f.youtube_url)) return alert("A valid YouTube URL is required — every Labs post needs a video.");
    setSaving(true);
    try {
      const body = { ...f, order: Number(f.order) || 0 };
      if (f.id) await adminFetch(`/admin/labs/${f.id}`, { method: "PUT", body });
      else await adminFetch("/admin/labs", { method: "POST", body });
      onSaved();
    } catch (e) {
      alert("Save failed: " + e.message);
    }
    setSaving(false);
  };

  return (
    <div className="rounded-2xl border border-black/10 bg-white p-6" data-testid="admin-labs-form">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{f.id ? "Edit post" : "New Labs post"}</h3>
        <div className="flex gap-2">
          <button className={ghost} onClick={onCancel}>Cancel</button>
          <button className={ember} onClick={save} disabled={saving} data-testid="admin-labs-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><span className={label}>Title</span><input className={input} value={f.title} onChange={s("title")} data-testid="admin-labs-title" /></div>
        <div className="sm:col-span-2"><span className={label}>YouTube URL (required)</span>
          <input className={input} value={f.youtube_url} onChange={s("youtube_url")} placeholder="https://www.youtube.com/watch?v=..." data-testid="admin-labs-youtube" />
          {thumb && <img src={thumb} alt="" className="mt-2 h-28 w-auto rounded-lg object-cover" />}
        </div>
        <div><span className={label}>Category</span><input className={input} value={f.category} onChange={s("category")} placeholder="AI Video, Copywriting…" /></div>
        <div><span className={label}>Author</span><input className={input} value={f.author} onChange={s("author")} /></div>
        <div><span className={label}>Read time</span><input className={input} value={f.read_time} onChange={s("read_time")} placeholder="5 min read" /></div>
        <div><span className={label}>Order</span><input className={input} type="number" value={f.order} onChange={s("order")} /></div>
      </div>
      <div className="mt-4"><span className={label}>Excerpt (one-line summary, shown on the tile)</span><textarea className={input} rows={2} value={f.excerpt} onChange={s("excerpt")} data-testid="admin-labs-excerpt" /></div>
      <div className="mt-4"><span className={label}>Content (body text — supports blank-line paragraphs)</span><textarea className={input} rows={10} value={f.content} onChange={s("content")} data-testid="admin-labs-content" /></div>
      <div className="mt-5 flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} data-testid="admin-labs-featured" />
          Featured (shown first)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="admin-labs-published" />
          Published (live on /labs)
        </label>
      </div>
    </div>
  );
}

function LabsManager() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const reload = () => adminFetch("/admin/labs").then(setPosts).catch(() => setPosts([]));
  useEffect(() => { reload(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await adminFetch(`/admin/labs/${id}`, { method: "DELETE" });
    reload();
  };

  const edit = async (p) => {
    try { setEditing(await adminFetch(`/admin/labs/${p.id}`)); }
    catch { setEditing(p); }
  };

  if (editing) {
    return (
      <LabsForm
        initial={editing.id ? editing : {}}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-labs-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#6B6358]">{posts.length} posts</p>
        <button className={ember} onClick={() => setEditing({})} data-testid="admin-labs-new">+ New post</button>
      </div>
      <div className="space-y-2">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center gap-4 rounded-xl border border-black/10 bg-white p-4" data-testid={`admin-labs-row-${p.slug}`}>
            <img src={youTubeThumb(p.youtube_url) || undefined} alt="" className="h-12 w-20 rounded object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{p.title}</p>
              <p className="truncate text-[13px] text-[#6B6358]">{p.category || "—"} · {p.author}</p>
            </div>
            {p.featured && <span className="rounded-full bg-[#04785C]/10 px-2.5 py-1 text-[11px] font-semibold text-[#04785C]">Featured</span>}
            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${p.published ? "bg-green-100 text-green-700" : "bg-black/10 text-[#6B6358]"}`}>{p.published ? "Published" : "Draft"}</span>
            <button className={ghost} onClick={() => edit(p)} data-testid={`admin-labs-edit-${p.slug}`}>Edit</button>
            <button className={ghost} onClick={() => del(p.id)}>Delete</button>
          </div>
        ))}
        {posts.length === 0 && (
          <p className="rounded-xl border border-dashed border-black/15 bg-white p-6 text-sm text-[#6B6358]">
            No Labs posts yet. Click "+ New post" — every post needs a YouTube video; its thumbnail becomes the tile cover.
          </p>
        )}
      </div>
    </div>
  );
}

const blankGallery = { title: "", caption: "", video_url: "", poster: "", content_types: [], orientation: "horizontal", featured: false, published: true, order: 0 };
const GALLERY_CONTENT_TYPES = ["Long-form", "Talking heads", "Advertisements"];

function GalleryForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState({ ...blankGallery, ...initial });
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ status: "" });
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const src = videoSource(f.video_url);
  const previewThumb = f.poster
    ? resolveMedia(f.poster)
    : src.kind === "youtube"
      ? youTubeThumb(f.video_url)
      : null;

  const fetchMeta = async () => {
    const url = (f.video_url || "").trim();
    if (!url) return;
    setMeta({ status: "loading" });
    try {
      const m = await adminFetch(`/admin/youtube-meta?url=${encodeURIComponent(url)}`);
      setF((prev) => ({
        ...prev,
        poster: m.thumbnail_url || m.thumbnail_fallback || prev.poster,
        title: prev.title || m.title || "",
      }));
      setMeta({ status: "ok" });
    } catch {
      setMeta({ status: "error" });
    }
  };

  const save = async () => {
    if (!f.title.trim()) return alert("Title is required");
    if (!f.video_url.trim() || !src.kind)
      return alert("A valid YouTube, Vimeo, or .mp4 video URL is required.");
    setSaving(true);
    try {
      const body = { ...f, order: Number(f.order) || 0 };
      if (f.id) await adminFetch(`/admin/gallery/${f.id}`, { method: "PUT", body });
      else await adminFetch("/admin/gallery", { method: "POST", body });
      onSaved();
    } catch (e) {
      alert("Save failed: " + e.message);
    }
    setSaving(false);
  };

  return (
    <div className="rounded-2xl border border-black/10 bg-white p-6" data-testid="admin-gallery-form">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{f.id ? "Edit reel item" : "New reel item"}</h3>
        <div className="flex gap-2">
          <button className={ghost} onClick={onCancel}>Cancel</button>
          <button className={ember} onClick={save} disabled={saving} data-testid="admin-gallery-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <div className="mt-5 space-y-4">
        <div><span className={label}>Title</span><input className={input} value={f.title} onChange={s("title")} data-testid="admin-gallery-title" /></div>
        <div><span className={label}>Caption (one line)</span><input className={input} value={f.caption} onChange={s("caption")} placeholder="A short line shown under the title" data-testid="admin-gallery-caption" /></div>
        <div>
          <span className={label}>Video URL (YouTube, Vimeo, or .mp4)</span>
          <div className="flex gap-2">
            <input className={input} value={f.video_url} onChange={s("video_url")} placeholder="https://youtu.be/…, https://vimeo.com/…, or …/clip.mp4" data-testid="admin-gallery-video" />
            <button type="button" className={`${ghost} whitespace-nowrap`} onClick={fetchMeta} data-testid="admin-gallery-fetch">Auto-fill</button>
          </div>
          {meta.status === "error" && <p className="mt-1 text-[12px] text-red-600">Couldn't fetch a thumbnail for that link.</p>}
          <p className="mt-1 text-[12px] text-[#6B6358]">
            YouTube/Vimeo thumbnails auto-fill. For a direct .mp4, add a Thumbnail image below.
          </p>
        </div>
        <div>
          <span className={label}>Thumbnail image (poster — required for .mp4)</span>
          <UploadField value={f.poster} onChange={(v) => setF({ ...f, poster: v })} accept="image/*" placeholder="https://…/thumb.jpg" />
        </div>
        {previewThumb && <img src={previewThumb} alt="" className="h-32 w-auto rounded-lg object-cover" />}

        <div>
          <span className={label}>Content type (multi-select — used for the /gallery filter)</span>
          <div className="flex flex-wrap gap-2" data-testid="admin-gallery-content-types">
            {GALLERY_CONTENT_TYPES.map((ct) => {
              const on = (f.content_types || []).includes(ct);
              return (
                <button
                  key={ct}
                  type="button"
                  onClick={() =>
                    setF({
                      ...f,
                      content_types: on
                        ? f.content_types.filter((x) => x !== ct)
                        : [...(f.content_types || []), ct],
                    })
                  }
                  className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${on ? "border-[#04785C] bg-[#04785C] text-white" : "border-black/15 bg-white text-[#282D32] hover:border-black/30"}`}
                  data-testid={`admin-gallery-ct-${ct.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {ct}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className={label}>Orientation (player aspect ratio)</span>
            <select className={input} value={f.orientation || "horizontal"} onChange={s("orientation")} data-testid="admin-gallery-orientation">
              <option value="horizontal">Horizontal (16:9)</option>
              <option value="vertical">Vertical (9:16)</option>
            </select>
          </div>
          <div><span className={label}>Order (lower shows first)</span><input className={input} type="number" value={f.order} onChange={s("order")} data-testid="admin-gallery-order" /></div>
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} data-testid="admin-gallery-featured" />
            Featured (shown first)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="admin-gallery-published" />
            Published (live on /gallery)
          </label>
        </div>
      </div>
    </div>
  );
}

function GalleryManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const reload = () => adminFetch("/admin/gallery").then(setItems).catch(() => setItems([]));
  useEffect(() => { reload(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this reel item?")) return;
    await adminFetch(`/admin/gallery/${id}`, { method: "DELETE" });
    reload();
  };

  const edit = async (it) => {
    try { setEditing(await adminFetch(`/admin/gallery/${it.id}`)); }
    catch { setEditing(it); }
  };

  if (editing) {
    return (
      <GalleryForm
        initial={editing.id ? editing : {}}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-gallery-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#6B6358]">{items.length} reel items</p>
        <button className={ember} onClick={() => setEditing({})} data-testid="admin-gallery-new">+ New reel item</button>
      </div>
      <div className="space-y-2">
        {items.map((it) => {
          const src = videoSource(it.video_url);
          const thumb = resolveMedia(it.poster) || (src.kind === "youtube" ? youTubeThumb(it.video_url) : null);
          return (
            <div key={it.id} className="flex items-center gap-4 rounded-xl border border-black/10 bg-white p-4" data-testid={`admin-gallery-row-${it.id}`}>
              {thumb ? <img src={thumb} alt="" className="h-12 w-20 rounded object-cover" /> : <div className="h-12 w-20 rounded bg-black/10" />}
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{it.title}</p>
                <p className="truncate text-[13px] text-[#6B6358]">{it.caption || "—"} · {src.kind || "unknown"}</p>
              </div>
              {it.featured && <span className="rounded-full bg-[#04785C]/10 px-2.5 py-1 text-[11px] font-semibold text-[#04785C]">Featured</span>}
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${it.published ? "bg-green-100 text-green-700" : "bg-black/10 text-[#6B6358]"}`}>{it.published ? "Published" : "Draft"}</span>
              <span className="text-[12px] text-[#6B6358]">#{it.order}</span>
              <button className={ghost} onClick={() => edit(it)} data-testid={`admin-gallery-edit-${it.id}`}>Edit</button>
              <button className={ghost} onClick={() => del(it.id)}>Delete</button>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="rounded-xl border border-dashed border-black/15 bg-white p-6 text-sm text-[#6B6358]">
            No reel items yet. Click "+ New reel item" — paste a YouTube, Vimeo, or .mp4 link, add a title and caption, publish.
          </p>
        )}
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // project object or {} for new

  const reload = async () => {
    try { setProjects(await adminFetch("/admin/projects")); }
    catch (e) { if (e.message === "unauthorized") onLogout(); }
  };
  useEffect(() => { reload(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await adminFetch(`/admin/projects/${id}`, { method: "DELETE" });
    reload();
  };

  if (editing) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-10">
        <ProjectForm initial={editing.id ? editing : {}} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="grid grid-cols-2 gap-[3px]">{[0, 1, 2, 3].map((i) => <span key={i} className="block h-2 w-2 rounded-[2px]" style={{ background: i === 0 || i === 3 ? "#04785C" : "#1E2225" }} />)}</span>
          <h1 className="text-lg font-bold tracking-tight">The AI Dept · Portfolio CMS</h1>
        </div>
        <button className={ghost} onClick={onLogout}>Log out</button>
      </div>

      <div className="mt-8 flex gap-2">
        <button className={tab === "projects" ? ember : ghost} onClick={() => setTab("projects")}>Projects</button>
        <button className={tab === "gallery" ? ember : ghost} onClick={() => setTab("gallery")} data-testid="admin-tab-gallery">Video Gallery</button>
        <button className={tab === "categories" ? ember : ghost} onClick={() => setTab("categories")}>Categories</button>
        <button className={tab === "demo" ? ember : ghost} onClick={() => setTab("demo")} data-testid="admin-tab-demo">Demo</button>
        <button className={tab === "labs" ? ember : ghost} onClick={() => setTab("labs")} data-testid="admin-tab-labs">Labs</button>
      </div>

      <div className="mt-8">
        {tab === "gallery" ? <GalleryManager /> : tab === "categories" ? <Categories /> : tab === "demo" ? <DemoSection /> : tab === "labs" ? <LabsManager /> : (
          <>
            <div className="mb-5 flex items-center justify-between">
              <p className="text-sm text-[#6B6358]">{projects.length} projects</p>
              <button className={ember} onClick={() => setEditing({})} data-testid="admin-new-project">+ New project</button>
            </div>
            <div className="space-y-2">
              {projects.map((p) => (
                <div key={p.id} className="flex items-center gap-4 rounded-xl border border-black/10 bg-white p-4" data-testid={`admin-row-${p.slug}`}>
                  <img src={resolveMedia(p.poster) || undefined} alt="" className="h-12 w-16 rounded object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold">{p.title}</p>
                    <p className="truncate text-[13px] text-[#6B6358]">{p.client}{(p.tags && p.tags[0]) ? ` · ${p.tags[0]}` : p.category ? ` · ${p.category}` : ""}</p>
                  </div>
                  {p.featured && <span className="rounded-full bg-[#04785C]/10 px-2.5 py-1 text-[11px] font-semibold text-[#04785C]">Featured</span>}
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${p.published ? "bg-green-100 text-green-700" : "bg-black/10 text-[#6B6358]"}`}>{p.published ? "Published" : "Draft"}</span>
                  <span className="text-[12px] text-[#6B6358]">#{p.order}</span>
                  <button className={ghost} onClick={() => setEditing(p)} data-testid={`admin-edit-${p.slug}`}>Edit</button>
                  <button className={ghost} onClick={() => del(p.id)}>Delete</button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Login({ onIn }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw }),
      });
      if (!r.ok) throw new Error("Incorrect password");
      const d = await r.json();
      setToken(d.token);
      onIn();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF8F4] px-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-black/10 bg-white p-8 shadow-sm">
        <h1 className="text-xl font-bold tracking-tight">Portfolio CMS</h1>
        <p className="mt-1 text-sm text-[#6B6358]">Enter the admin password.</p>
        <input type="password" className={`${input} mt-6`} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" data-testid="admin-password" autoFocus />
        {err && <p className="mt-2 text-[13px] text-[#04785C]">{err}</p>}
        <button className={`${ember} mt-5 w-full`} disabled={busy} data-testid="admin-login-btn">{busy ? "…" : "Enter"}</button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const t = getToken();
    if (!t) { setChecking(false); return; }
    adminFetch("/admin/verify").then(() => setAuthed(true)).catch(() => clearToken()).finally(() => setChecking(false));
  }, []);

  const logout = () => { clearToken(); setAuthed(false); };

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-[#FAF8F4] text-[#6B6358]">…</div>;
  return authed ? <Dashboard onLogout={logout} /> : <Login onIn={() => setAuthed(true)} />;
}
